import React, { useMemo } from 'react';
import type {
  ColDef,
  ValueFormatterParams,
  ValueGetterParams,
  ValueSetterParams,
} from 'ag-grid-community';
import { LopCrudPage } from '../../components/LopCrudPage';
import { getCategories, saveBatchCategories } from '../../api/categories';
import { useIsaAssetTypes } from '../../hooks/useIsaAssetTypes';
import { useGroupingLevels } from '../../hooks/useGroupingLevels';
import type { Category } from '../../types/Category';
import { t } from '../../i18n/t';

const newCategoryDefaults = (): Partial<Category> => ({
  categoryId: 0,
  categoryName: '',
  isDetailed4IsaReport: false,
  isaCode: null,
  groupingLevelId: null,
});

export function CategoriesPage() {
  // Lazy load: both lookup lists are fetched only when this page mounts.
  const { data: isaAssetTypes, loading: isaLoading, error: isaError } =
    useIsaAssetTypes();
  const {
    data: groupingLevels,
    loading: groupingLevelsLoading,
    error: groupingLevelsError,
  } = useGroupingLevels();

  const columnDefs = useMemo<ColDef<Category>[]>(() => {
    if (!isaAssetTypes || !groupingLevels) return [];
    const noneLabel = t('categories.noneOption');
    // ISA asset-type names are NOT unique (e.g. "ספוט" at codes 56 and 216),
    // so the dropdown is keyed by a unique "<code> - <name>" label.
    const dropdownValues = [
      noneLabel,
      ...isaAssetTypes.map((a) => `${a.isaCode} - ${a.name}`),
    ];
    const labelByCode = new Map<number, string>(
      isaAssetTypes.map((a) => [a.isaCode, `${a.isaCode} - ${a.name}`]),
    );
    const codeByLabel = new Map<string, number>(
      isaAssetTypes.map((a) => [`${a.isaCode} - ${a.name}`, a.isaCode]),
    );

    // Grouping-level names are keyed by "<id> - <name>" per the standard
    // id-dropdown recipe (see CalcMethodMap/AssetsPage). Nullable FK, so the
    // dropdown includes the "none" option.
    const groupingLevelValues = [
      noneLabel,
      ...groupingLevels.map(
        (g) => `${g.groupingLevelId} - ${g.groupingLevelName}`,
      ),
    ];
    const groupingLevelLabelById = new Map<number, string>(
      groupingLevels.map((g) => [
        g.groupingLevelId,
        `${g.groupingLevelId} - ${g.groupingLevelName}`,
      ]),
    );
    const groupingLevelIdByLabel = new Map<string, number>(
      groupingLevels.map((g) => [
        `${g.groupingLevelId} - ${g.groupingLevelName}`,
        g.groupingLevelId,
      ]),
    );

    return [
      {
        field: 'categoryId',
        headerName: t('categories.columnCategoryId'),
        width: 80,
        editable: false,
        sort: 'asc',
        valueFormatter: (p: ValueFormatterParams<Category>) => {
          const isAdded =
            (p.data as { __status?: string } | undefined)?.__status === 'added';
          if (isAdded) return '';
          return p.value == null ? '' : String(p.value);
        },
      },
      {
        field: 'categoryName',
        headerName: t('categories.columnCategoryName'),
        flex: 1,
        editable: true,
      },
      {
        field: 'isDetailed4IsaReport',
        headerName: t('categories.columnIsDetailed4IsaReport'),
        width: 160,
        editable: true,
        cellDataType: 'boolean',
      },
      {
        field: 'isaCode',
        headerName: t('categories.columnIsaCode'),
        width: 260,
        editable: true,
        cellDataType: 'text',
        cellEditor: 'agSelectCellEditor',
        cellEditorParams: { values: dropdownValues },
        valueGetter: (p: ValueGetterParams<Category>) => {
          if (!p.data) return noneLabel;
          if (p.data.isaCode == null) return noneLabel;
          return labelByCode.get(p.data.isaCode) ?? noneLabel;
        },
        valueSetter: (p: ValueSetterParams<Category>) => {
          if (!p.data) return false;
          if (p.newValue === noneLabel) {
            p.data.isaCode = null;
            return true;
          }
          const newCode = codeByLabel.get(p.newValue);
          if (newCode === undefined) return false;
          p.data.isaCode = newCode;
          return true;
        },
      },
      {
        field: 'groupingLevelId',
        headerName: t('categories.columns.groupingLevelId'),
        width: 260,
        editable: true,
        cellDataType: 'text',
        cellEditor: 'agSelectCellEditor',
        cellEditorParams: { values: groupingLevelValues },
        valueGetter: (p: ValueGetterParams<Category>) => {
          if (!p.data) return noneLabel;
          if (p.data.groupingLevelId == null) return noneLabel;
          // Orphan-id fallback: a value not present in the loaded lookup
          // (e.g. stale/dirty data) still renders instead of disappearing.
          return (
            groupingLevelLabelById.get(p.data.groupingLevelId) ??
            String(p.data.groupingLevelId)
          );
        },
        valueSetter: (p: ValueSetterParams<Category>) => {
          if (!p.data) return false;
          if (p.newValue === noneLabel) {
            p.data.groupingLevelId = null;
            return true;
          }
          const newId = groupingLevelIdByLabel.get(p.newValue);
          if (newId === undefined) return false;
          p.data.groupingLevelId = newId;
          return true;
        },
      },
    ];
  }, [isaAssetTypes, groupingLevels]);

  const error = isaError ?? groupingLevelsError;
  if (error) {
    return (
      <div style={{ padding: 16 }}>
        <div
          style={{
            color: 'var(--color-error-text)',
            background: 'var(--color-error-bg)',
            padding: '8px 12px',
            borderRadius: 4,
          }}
        >
          {t('categories.errorPrefix', { message: error })}
        </div>
      </div>
    );
  }

  if (isaLoading || groupingLevelsLoading || !isaAssetTypes || !groupingLevels) {
    return <div style={{ padding: 16, color: 'var(--color-text-secondary)' }}>{t('common.loading')}</div>;
  }

  return (
    <LopCrudPage<Category>
      resourceCode="categories"
      titleKey="categories.title"
      idField="categoryId"
      getRows={getCategories}
      saveBatch={(added, modified, deletedIds) =>
        saveBatchCategories(added, modified, deletedIds as number[])
      }
      columnDefs={columnDefs}
      newRowDefaults={newCategoryDefaults}
    />
  );
}
